import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronUp, ChevronDown, AlertTriangle, CheckCircle, Download } from 'lucide-react';
import { inventoryApi } from '../../services/inventoryApi';
import type { DrillDownContext, InventoryTransaction, InventoryTransactionSummary } from '../../types/inventory';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    item: DrillDownContext;
    /** Optional: when provided, restricts transactions to a single lot */
    lotCode?: string | null;
}

function formatDate(dateStr: string | null): string {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('de-CH');
}

function formatNumber(num: number | null | undefined): string {
    if (num == null) return '-';
    return new Intl.NumberFormat('de-CH', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(num);
}

function formatCurrency(num: number | null | undefined): string {
    if (num == null) return '-';
    return new Intl.NumberFormat('de-CH', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(num);
}

type SortKey = 'PostingDate' | 'TransactionType' | 'Quantity' | 'CostLCY' | 'LotCode' | 'ContactName';
type SortDir = 'asc' | 'desc';

function SortHeader({ label, sortKey, currentKey, currentDir, onSort, align }: {
    label: string;
    sortKey: SortKey;
    currentKey: SortKey;
    currentDir: SortDir;
    onSort: (key: SortKey) => void;
    align?: 'left' | 'right';
}) {
    const isActive = currentKey === sortKey;
    return (
        <th
            className={`sticky top-0 px-3 py-2 text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700 bg-white ${
                align === 'right' ? 'text-right' : 'text-left'
            }`}
            onClick={() => onSort(sortKey)}
        >
            <span className="inline-flex items-center gap-0.5">
                {label}
                <span className="inline-flex flex-col -space-y-1">
                    <ChevronUp className={`h-3 w-3 ${isActive && currentDir === 'asc' ? 'text-gray-900' : 'text-gray-300'}`} />
                    <ChevronDown className={`h-3 w-3 ${isActive && currentDir === 'desc' ? 'text-gray-900' : 'text-gray-300'}`} />
                </span>
            </span>
        </th>
    );
}

function TransactionTypeBadge({ type }: { type: string }) {
    const colors: Record<string, string> = {
        'Purchase': 'bg-green-100 text-green-700',
        'Sale': 'bg-red-100 text-red-700',
        'Transfer': 'bg-blue-100 text-blue-700',
        'Positive Adjmt.': 'bg-emerald-100 text-emerald-700',
        'Negative Adjmt.': 'bg-orange-100 text-orange-700',
    };
    return (
        <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${colors[type] || 'bg-gray-100 text-gray-700'}`}>
            {type}
        </span>
    );
}

export function TransactionsModal({ isOpen, onClose, item, lotCode }: Props) {
    const [transactions, setTransactions] = useState<InventoryTransaction[]>([]);
    const [summary, setSummary] = useState<InventoryTransactionSummary | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [sortKey, setSortKey] = useState<SortKey>('PostingDate');
    const [sortDir, setSortDir] = useState<SortDir>('desc');

    useEffect(() => {
        if (!isOpen) return;
        setLoading(true);
        setError(null);

        inventoryApi.getTransactions(item.ItemID, lotCode ?? null)
            .then(data => {
                setTransactions(data.transactions);
                setSummary(data.summary);
            })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [isOpen, item.ItemID, lotCode]);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        if (isOpen) document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    const sorted = useMemo(() => {
        return [...transactions].sort((a, b) => {
            const aVal = a[sortKey];
            const bVal = b[sortKey];
            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return 1;
            if (bVal == null) return -1;
            const cmp = typeof aVal === 'number'
                ? aVal - (bVal as number)
                : String(aVal).localeCompare(String(bVal));
            return sortDir === 'asc' ? cmp : -cmp;
        });
    }, [transactions, sortKey, sortDir]);

    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDir(key === 'PostingDate' ? 'desc' : 'asc');
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />

            {/* Modal */}
            <div className="relative z-10 flex max-h-[85vh] w-full max-w-5xl flex-col rounded-xl bg-white shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">
                            Transactions
                        </h2>
                        <p className="mt-0.5 text-sm text-gray-500">
                            {item.ProductName} ({item.CompanyCode}) &middot; {item.WarehouseName}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={async () => {
                                try {
                                    await inventoryApi.exportTransactions(item.ItemID, lotCode ?? null);
                                } catch (err) {
                                    console.error('Export failed:', err);
                                }
                            }}
                            disabled={transactions.length === 0}
                            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            title="Export to Excel"
                        >
                            <Download className="h-4 w-4" /> Excel
                        </button>
                        <button
                            onClick={onClose}
                            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                {/* Summary bar */}
                {summary && (
                    <div className="flex items-center gap-6 border-b border-gray-100 bg-gray-50 px-6 py-3 text-sm">
                        <div>
                            <span className="text-gray-500">Transactions:</span>{' '}
                            <span className="font-medium">{summary.transactionCount}</span>
                        </div>
                        <div>
                            <span className="text-gray-500">Sum Qty:</span>{' '}
                            <span className="font-mono font-medium">{formatNumber(summary.totalQuantity)}</span>
                        </div>
                        <div>
                            <span className="text-gray-500">Stock Qty:</span>{' '}
                            <span className="font-mono font-medium">{formatNumber(summary.stockQuantity)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                            {summary.matches ? (
                                <>
                                    <CheckCircle className="h-4 w-4 text-green-500" />
                                    <span className="text-green-700 font-medium">Match</span>
                                </>
                            ) : (
                                <>
                                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                                    <span className="text-amber-700 font-medium">
                                        Diff: {formatNumber(summary.totalQuantity - summary.stockQuantity)}
                                    </span>
                                </>
                            )}
                        </div>
                        <div className="ml-auto">
                            <span className="text-gray-500">Total Cost:</span>{' '}
                            <span className="font-mono font-medium">{formatCurrency(summary.totalCost)}</span>
                        </div>
                    </div>
                )}

                {/* Content */}
                <div className="flex-1 overflow-auto">
                    {loading ? (
                        <div className="flex items-center justify-center py-16">
                            <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                        </div>
                    ) : error ? (
                        <div className="px-6 py-8 text-center text-sm text-red-600">{error}</div>
                    ) : transactions.length === 0 ? (
                        <div className="px-6 py-12 text-center text-sm text-gray-500">
                            No transactions found for this item.
                        </div>
                    ) : (
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead>
                                <tr>
                                    <SortHeader label="Date" sortKey="PostingDate" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                                    <SortHeader label="Type" sortKey="TransactionType" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                                    <SortHeader label="Quantity" sortKey="Quantity" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} align="right" />
                                    <SortHeader label="Cost (LCY)" sortKey="CostLCY" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} align="right" />
                                    <SortHeader label="Lot" sortKey="LotCode" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                                    <SortHeader label="Contact" sortKey="ContactName" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                                    <th className="sticky top-0 bg-white px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                        Doc Nr
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {sorted.map(tx => {
                                    // Extract document number from TransactionID (strip company prefix)
                                    const docNr = tx.TransactionID?.includes('-')
                                        ? tx.TransactionID.substring(tx.TransactionID.indexOf('-') + 1)
                                        : tx.TransactionID;

                                    return (
                                        <tr key={tx.TransactionID} className="hover:bg-gray-50">
                                            <td className="whitespace-nowrap px-3 py-1.5 text-gray-700">
                                                {formatDate(tx.PostingDate)}
                                            </td>
                                            <td className="px-3 py-1.5">
                                                <TransactionTypeBadge type={tx.TransactionType} />
                                            </td>
                                            <td className={`whitespace-nowrap px-3 py-1.5 text-right font-mono ${
                                                tx.Quantity > 0 ? 'text-green-700' : tx.Quantity < 0 ? 'text-red-700' : 'text-gray-400'
                                            }`}>
                                                {formatNumber(tx.Quantity)}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-1.5 text-right font-mono text-gray-700">
                                                {formatCurrency(tx.CostLCY)}
                                            </td>
                                            <td className="px-3 py-1.5 text-gray-600">
                                                {tx.LotCode || ''}
                                            </td>
                                            <td className="px-3 py-1.5 text-gray-700" title={tx.ContactID ?? ''}>
                                                {tx.ContactName || tx.ContactID || ''}
                                            </td>
                                            <td className="px-3 py-1.5 text-gray-500 font-mono text-xs">
                                                {docNr}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}
